import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  Sparkles, Cpu, FileText, MessageCircle, Wrench, BarChart3, Receipt,
  Bot, ArrowRight, CheckCircle2,
} from "lucide-react";

/**
 * SmartFeaturesUpsell — high-conversion subscription marketing card for
 * the landlord dashboard. Visible ONLY to non-subscribed landlords; sells
 * the monthly subscription by showcasing the 8 smart features.
 *
 * Designed so it can drop into any portal page; the component reads
 * subscription status itself.
 */
const SMART_BENEFITS = [
  { icon: Bot,         title: "Smart Maintenance Triage",  body: "Smart-grades severity + drafts the reply." },
  { icon: MessageCircle, title: "Smart Inbox Operator",    body: "Email, SMS & chat — one calm reply." },
  { icon: Receipt,     title: "Smart AR/AP Bookkeeper",    body: "Reconciles bank, chases arrears." },
  { icon: FileText,    title: "Smart Lease Generator",     body: "Provincial-compliant in 90 seconds." },
  { icon: BarChart3,   title: "Smart Rent Benchmarking",   body: "Always know what to charge." },
  { icon: Wrench,      title: "Smart Vendor Dispatch",     body: "Routes to the right pro automatically." },
  { icon: Cpu,         title: "Smart Building Hub",        body: "Locks, leaks, thermostats — one console." },
  { icon: Sparkles,    title: "Smart Visibility Score",    body: "Daily SEO + smart-search ranking checks." },
];

export default function SmartFeaturesUpsell() {
  const { user } = useAuth();
  const isSubscribed = user?.subscription_status === "active"
    || user?.subscription_status === "trialing";
  if (isSubscribed || user?.role !== "landlord") return null;

  return (
    <div className="my-6 rounded-3xl overflow-hidden border border-emerald-200 shadow-md bg-gradient-to-br from-white via-emerald-50 to-emerald-100 text-slate-900" data-testid="smart-features-upsell">
      <div className="px-4 sm:px-6 md:px-8 py-6 sm:py-7 md:py-9 relative">
        <div className="absolute -top-12 -right-12 w-60 h-60 rounded-full bg-emerald-300/25 blur-3xl pointer-events-none"/>
        <div className="absolute -bottom-16 -left-16 w-72 h-72 rounded-full bg-sky-300/20 blur-3xl pointer-events-none"/>

        <div className="relative">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 border border-emerald-300 text-[10px] font-bold tracking-[0.22em] uppercase text-emerald-800">
            <Sparkles className="w-3 h-3"/> Subscribe to unlock smart features
          </div>
          <h3 className="font-display text-xl sm:text-2xl md:text-3xl font-extrabold mt-3 leading-tight tracking-tight max-w-2xl">
            8 smart employees that run your portfolio 24/7 — for less than a leasing fee.
          </h3>
          <p className="text-sm md:text-base text-slate-700 mt-2 max-w-2xl">
            Every active landlord plan unlocks the full smart suite. They answer calls, file workpapers,
            book vendors, draft leases, and surface what needs your attention — so you can focus on the
            5 % that needs a human.
          </p>

          <div className="mt-5 sm:mt-6 grid grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-3" data-testid="smart-features-upsell-grid">
            {SMART_BENEFITS.map((b, i) => {
              const Icon = b.icon;
              return (
                <div key={i} className="rounded-2xl bg-white border border-emerald-200 hover:border-emerald-300 hover:shadow-sm transition-all p-3 sm:p-3.5">
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-emerald-600 text-white flex items-center justify-center shadow-sm">
                    <Icon className="w-4 h-4"/>
                  </div>
                  <div className="font-display text-xs sm:text-[13px] font-bold mt-2 sm:mt-2.5 leading-tight text-slate-900">{b.title}</div>
                  <div className="text-[10px] sm:text-[11px] text-slate-600 mt-1 leading-snug">{b.body}</div>
                </div>
              );
            })}
          </div>

          <div className="mt-5 sm:mt-6 flex flex-wrap items-center gap-3 sm:gap-4">
            <Link to="/billing" data-testid="smart-features-upsell-cta"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm transition-colors shadow-lg shadow-emerald-500/20">
              Choose a plan <ArrowRight className="w-4 h-4"/>
            </Link>
            <div className="flex items-center gap-2 text-[11px] sm:text-[12px] text-slate-600">
              <CheckCircle2 className="w-4 h-4 text-emerald-600"/> Cancel any time · Cancel anytime · Save ~6h / week
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
